<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import BroadcastMessage from './BroadcastMessage.vue';
import BroadcastFilterList from './BroadcastFilterList.vue';
import BroadcastErrorList from './BroadcastErrorList.vue';
import BroadcastTargetSpec from './BroadcastTargetSpec.vue';
import { formatDate } from 'dashboard/helper/dateHelper';

const props = defineProps({
  broadcast: {
    type: Object,
    required: true,
  },
});

const { t } = useI18n();
const isExpanded = ref(false);

const toggleExpanded = () => {
  isExpanded.value = !isExpanded.value;
};

const conversationIds = computed(
  () => props.broadcast.filters?.conversation_ids || []
);
const messagePayload = computed(() => props.broadcast.message_payload || {});
const targetSpec = computed(() => props.broadcast.target_spec || {});
const hasTargetSpec = computed(
  () => targetSpec.value.version && targetSpec.value.sources?.length
);

const failedItems = computed(() => {
  if (!props.broadcast.failed) return [];
  if (Array.isArray(props.broadcast.failed)) return props.broadcast.failed;
  return Object.values(props.broadcast.failed);
});

const failedCount = computed(() => failedItems.value.length);

const sentPercent = computed(() => {
  if (!props.broadcast.total_targets) return 0;
  const percent =
    (props.broadcast.sent_count / props.broadcast.total_targets) * 100;
  return Math.min(Math.max(Math.round(percent), 0), 100);
});

const failedPercent = computed(() => {
  if (!props.broadcast.total_targets) return 0;
  const percent = (failedCount.value / props.broadcast.total_targets) * 100;
  return Math.min(Math.max(Math.round(percent), 0), 100);
});

const statusColor = computed(() => {
  switch (props.broadcast.status) {
    case 'completed':
      return 'text-green-600 bg-green-50';
    case 'sending':
      return 'text-blue-600 bg-blue-50';
    case 'failed':
      return 'text-red-600 bg-red-50';
    default:
      return 'text-n-slate-11 bg-n-alpha-2';
  }
});
</script>

<template>
  <div
    class="flex flex-col w-full gap-4 shadow outline-1 outline outline-n-container rounded-xl bg-n-solid-2 px-6 py-5"
  >
    <div class="flex justify-between items-start">
      <div>
        <h3 class="text-base font-medium text-n-slate-12">
          {{ broadcast.title }}
        </h3>
      </div>
      <div
        class="px-2 py-1 rounded text-xs font-medium capitalize"
        :class="statusColor"
      >
        {{ broadcast.status }}
      </div>
    </div>

    <div class="flex w-full bg-n-strong h-2 rounded-full overflow-hidden">
      <div
        class="bg-green-100 h-full transition-all duration-500"
        :style="{ width: `${sentPercent}%` }"
      />
      <div
        class="bg-red-400 h-full transition-all duration-500"
        :style="{ width: `${failedPercent}%` }"
      />
    </div>
    <div class="grid grid-cols-2 sm:grid-cols-4 gap-4 pt-2">
      <div class="flex flex-col">
        <span class="text-xs text-n-slate-11">{{
          t('BROADCASTS.TOTAL_TARGETS')
        }}</span>
        <span class="text-sm font-medium text-n-slate-12">{{
          broadcast.total_targets
        }}</span>
      </div>
      <div class="flex flex-col">
        <span class="text-xs text-n-slate-11">{{ t('BROADCASTS.SENT') }}</span>
        <span class="text-sm font-medium text-n-slate-12">{{
          broadcast.sent_count
        }}</span>
      </div>
      <div class="flex flex-col">
        <span class="text-xs text-n-slate-11">{{
          t('BROADCASTS.PENDING')
        }}</span>
        <span class="text-sm font-medium text-n-slate-12">{{
          broadcast.pending_count
        }}</span>
      </div>
      <div class="flex flex-col">
        <span class="text-xs text-n-slate-11">{{
          t('BROADCASTS.FAILED')
        }}</span>
        <span
          class="text-sm font-medium"
          :class="failedCount > 0 ? 'text-red-500' : 'text-n-slate-12'"
        >
          {{ failedCount }}
        </span>
      </div>
    </div>

    <p v-if="broadcast.skipped_count" class="mb-0 text-sm text-n-slate-11">
      {{
        t('BROADCASTS.BLACKLIST.SKIPPED', { count: broadcast.skipped_count })
      }}
    </p>

    <div
      class="border-t border-n-strong pt-3 flex flex-col sm:flex-row gap-4 text-xs text-n-slate-11"
    >
      <div class="flex flex-col sm:flex-row gap-4">
        <div v-if="broadcast.started_at">
          {{ t('BROADCASTS.STARTED') }}
          {{ formatDate(broadcast.started_at, 'ru-RU') }}
        </div>
        <div v-if="broadcast.completed_at">
          {{ t('BROADCASTS.COMPLETED') }}
          {{ formatDate(broadcast.completed_at, 'ru-RU') }}
        </div>
        <div v-else>
          {{ t('BROADCASTS.CREATED') }}
          {{ formatDate(broadcast.created_at, 'ru-RU') }}
        </div>
      </div>
      <button
        class="flex items-center gap-2 text-xs font-medium text-n-slate-11 transition hover:text-n-slate-12 sm:ml-auto"
        type="button"
        :aria-expanded="isExpanded"
        @click="toggleExpanded"
      >
        <span>
          {{
            isExpanded
              ? t('BROADCASTS.HIDE_DETAILS')
              : t('BROADCASTS.SHOW_DETAILS')
          }}
        </span>
        <span
          class="w-4 h-4 transition-transform duration-200 i-lucide-chevron-down"
          :class="{ 'rotate-180': isExpanded }"
        />
      </button>
    </div>

    <div
      v-if="isExpanded"
      class="border-t border-n-strong pt-4 flex flex-col gap-6"
    >
      <BroadcastMessage :payload="messagePayload" />

      <div class="flex flex-col gap-3">
        <h4
          class="text-xs font-semibold uppercase tracking-wide text-n-slate-11"
        >
          {{
            hasTargetSpec
              ? t('BROADCASTS.TARGET_SPEC.TITLE')
              : t('BROADCASTS.FILTERS')
          }}
        </h4>
        <BroadcastTargetSpec v-if="hasTargetSpec" :target-spec="targetSpec" />
        <BroadcastFilterList v-else :conversation-ids="conversationIds" />
      </div>

      <BroadcastErrorList :failed-items="failedItems" />
    </div>
  </div>
</template>
